import React from 'react';
import axios from 'axios';
import uuid from 'react-uuid';
import { useDispatch, useSelector } from 'react-redux';
import { FaTrash } from 'react-icons/fa';
import { BtnSimple } from '../form/btn.Simple';
import { Checkbox } from '../form/checkbox';
import { InputSimple } from '../form/input.Simple';
import {
  SettingsInput,
  SettingsRow,
  SettingsSelect,
  SettingsTextArea,
  WidgetSettingsCollapsibleSection,
  WidgetSettingsRoot,
} from '../form/settings.Widget';
import { ModalGlobal } from '../modal/modal.Global';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { getSearch, upsertItemGenerator, update } from '../../services/item';
import { setOpenModales } from '../../store/global';
import {
  applyGeneratorGroupRules,
  normalizeGeneratorConfig,
  sanitizeGeneratorConfigForStorage,
  sanitizeGeneratorEntriesForStorage,
} from '../../utils/generator';
import { GeneratorSettingsLevel2 } from './generator.SettingsLevel2';

export const GeneratorSettings = props => {
  const { itemId, generatorId, generator, canEdit } = props;
  const item = useSelector(state => state.items?.[itemId]);
  const allItems = useSelector(state => state.items);
  const dispatch = useDispatch();
  const [openGroupControlId, setOpenGroupControlId] = React.useState(null);
  const [openProfileOptionsById, setOpenProfileOptionsById] = React.useState(
    {},
  );
  const [profileSearchQuery, setProfileSearchQuery] = React.useState('');
  const [templateQuery, setTemplateQuery] = React.useState('');
  const [templateResults, setTemplateResults] = React.useState([]);
  const [templateItemFull, setTemplateItemFull] = React.useState(null);
  const [jsonBuffer, setJsonBuffer] = React.useState('');
  const [jsonStatus, setJsonStatus] = React.useState('');
  const [jsonError, setJsonError] = React.useState('');
  const [controlLinkQueryById, setControlLinkQueryById] = React.useState({});
  const [controlLinkResultsById, setControlLinkResultsById] = React.useState({});
  const jsonBufferRef = React.useRef('');

  const [draft, setDraft] = React.useState(
    normalizeGeneratorConfig(generator, generatorId),
  );
  const draftRef = React.useRef(draft);

  React.useEffect(() => {
    const next = normalizeGeneratorConfig(generator, generatorId);
    draftRef.current = next;
    setDraft(next);
  }, [generator, generatorId]);

  React.useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const templateItemFromStore = useSelector(state =>
    state.items?.[draft?.itemCreation?.templateItemId || ''],
  );
  const templateItem = templateItemFull || templateItemFromStore || null;
  const generatorOptions = React.useMemo(
    () =>
      (item?.generators || [])
        .map(entry => {
          const id = getId(entry);
          if (!id || id === generatorId) {
            return null;
          }
          const raw = entry?.[id] || {};
          const normalizedEntry = normalizeGeneratorConfig(raw, id);
          return {
            id,
            label: normalizedEntry?.label || 'Générateur',
            controls: Array.isArray(normalizedEntry?.controls)
              ? normalizedEntry.controls.map(control => ({
                  id: control.id,
                  label: control.label || 'Option',
                }))
              : [],
          };
        })
        .filter(Boolean),
    [item?.generators, generatorId],
  );

  React.useEffect(() => {
    const templateId = draft?.itemCreation?.templateItemId || null;
    if (!templateId) {
      setTemplateItemFull(null);
      return;
    }

    const hasCompleteShape =
      templateItemFromStore &&
      templateItemFromStore.__partial !== true &&
      Array.isArray(templateItemFromStore?.text) &&
      Array.isArray(templateItemFromStore?.has_num);

    if (hasCompleteShape) {
      setTemplateItemFull(templateItemFromStore);
      return;
    }

    let cancelled = false;
    const loadTemplate = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/${templateId}`,
        );
        if (cancelled) {
          return;
        }
        if (res?.data?._id) {
          setTemplateItemFull(res.data);
        } else {
          setTemplateItemFull(null);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplateItemFull(null);
        }
      }
    };

    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [draft?.itemCreation?.templateItemId, templateItemFromStore]);

  const searchableTemplateIn = React.useMemo(
    () => ({
      type: false,
      universe: true,
      world: true,
      campaign: true,
    }),
    [],
  );

  const persist = React.useCallback(
    nextGenerator => {
      if (!canEdit) {
        return;
      }
      const clonedGenerators = deepCloneArray(item?.generators || []);
      const sanitizedGenerators =
        sanitizeGeneratorEntriesForStorage(clonedGenerators);
      const idx = sanitizedGenerators.findIndex(obj => getId(obj) === generatorId);
      if (idx === -1) {
        return;
      }
      sanitizedGenerators[idx] = {
        [generatorId]: sanitizeGeneratorConfigForStorage(
          normalizeGeneratorConfig(nextGenerator, generatorId),
        ),
      };
      upsertItemGenerator(itemId, generatorId, sanitizedGenerators[idx][generatorId]).catch(
        () => update(`items/${itemId}`, 'generators', sanitizedGenerators),
      );
    },
    [canEdit, item?.generators, itemId, generatorId],
  );

  const updateDraft = React.useCallback(
    updater => {
      const prev = draftRef.current;
      const nextRaw =
        typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      const next = normalizeGeneratorConfig(nextRaw, generatorId);
      draftRef.current = next;
      setDraft(next);
      persist(next);
    },
    [generatorId, persist],
  );

  const setJsonBufferValue = React.useCallback(value => {
    const nextValue = typeof value === 'string' ? value : String(value ?? '');
    jsonBufferRef.current = nextValue;
    setJsonBuffer(nextValue);
  }, []);

  const searchTemplateItems = React.useCallback(
    async value => {
      const query = value?.trim?.() || '';
      if (!query) {
        setTemplateResults([]);
        return;
      }
      const res = await getSearch(
        query,
        item,
        searchableTemplateIn,
        undefined,
      );
      if (!Array.isArray(res)) {
        setTemplateResults([]);
        return;
      }
      setTemplateResults(
        res.filter(result => result?._id && result._id !== itemId),
      );
    },
    [item, itemId, searchableTemplateIn],
  );

  const getExportConfig = React.useCallback(() => {
    const normalized = normalizeGeneratorConfig(draftRef.current, generatorId);
    const cleanedControls = (normalized.controls || []).map(control => ({
      id: control.id,
      type: control.type || 'check',
      label: control.label || 'Option',
      default: control.default !== false,
      linkedItemId: control.linkedItemId || null,
    }));
    const cleanedGroups = (normalized.groups || []).map(group => ({
      id: group.id,
      label: group.label || 'Groupe',
      displayMode:
        group.displayMode === 'checkbox' || group.displayMode === 'dropdown'
          ? group.displayMode
          : 'buttons',
      controlIds: Array.isArray(group.controlIds) ? group.controlIds : [],
      fallbackControlId: group.fallbackControlId || null,
    }));
    const cleanedProfiles = (normalized.profiles || []).map(profile => ({
      id: profile.id,
      label: profile.label || 'Combinaison',
      conditionGroups: Array.isArray(profile.conditionGroups)
        ? profile.conditionGroups.map(group => ({
            id: group.id,
            mode: group.mode === 'any' ? 'any' : 'all',
            controlIds: Array.isArray(group.controlIds) ? group.controlIds : [],
          }))
        : [],
    }));
    const cleanedLibraries = (normalized.libraries || []).map(library => ({
      id: library.id,
      label: library.label || 'Table',
      entries: Array.isArray(library.entries) ? library.entries : [],
      entrySeparator: library.entrySeparator === 'newline' ? 'newline' : 'comma',
      profileIds: Array.isArray(library.profileIds) ? library.profileIds : [],
    }));
    const cleanedOutputs = (normalized.outputs || []).map(output => ({
      id: output.id,
      label: output.label || 'Sortie',
      target:
        output?.target && typeof output.target === 'object'
          ? output.target
          : { kind: 'text' },
      profileIds: Array.isArray(output.profileIds) ? output.profileIds : [],
      markdownTemplate:
        typeof output.markdownTemplate === 'string' ? output.markdownTemplate : '',
      builder: {
        libraryIds: Array.isArray(output?.builder?.libraryIds)
          ? output.builder.libraryIds
          : [],
        separator:
          typeof output?.builder?.separator === 'string'
            ? output.builder.separator
            : '',
        capitalize: output?.builder?.capitalize !== false,
      },
      number: {
        min: Number.isFinite(Number(output?.number?.min))
          ? Number(output.number.min)
          : 0,
        max: Number.isFinite(Number(output?.number?.max))
          ? Number(output.number.max)
          : 10,
        step:
          Number.isFinite(Number(output?.number?.step)) &&
          Number(output.number.step) > 0
            ? Number(output.number.step)
            : 1,
      },
      sourceGeneratorId:
        typeof output?.sourceGeneratorId === 'string' &&
        output.sourceGeneratorId.trim().length > 0
          ? output.sourceGeneratorId
          : null,
      sourceControlIds: Array.isArray(output?.sourceControlIds)
        ? output.sourceControlIds.filter(Boolean)
        : [],
    }));

    return {
      version: Number.isFinite(Number(normalized.version))
        ? Number(normalized.version)
        : 1,
      label: normalized.label || 'Générateur',
      controls: cleanedControls,
      groups: cleanedGroups,
      profiles: cleanedProfiles,
      libraries: cleanedLibraries,
      outputs: cleanedOutputs,
      runtime:
        normalized.runtime && typeof normalized.runtime === 'object'
          ? normalized.runtime
          : { controlsState: {} },
      itemCreation:
        normalized.itemCreation && typeof normalized.itemCreation === 'object'
          ? normalized.itemCreation
          : {
              enabled: true,
              openInSidebar: true,
              templateItemId: null,
              outputMapping: {},
            },
      // Runtime generation result is not config.
      lastResult: null,
    };
  }, [generatorId]);

  const exportConfigToBuffer = React.useCallback(() => {
    try {
      const payload = getExportConfig();
      const json = JSON.stringify(payload, null, 2);
      setJsonBufferValue(json);
      setJsonError('');
      setJsonStatus('JSON exporté dans l’éditeur');
      return json;
    } catch (error) {
      setJsonError("Impossible d'exporter la configuration");
      setJsonStatus('');
      return null;
    }
  }, [getExportConfig, setJsonBufferValue]);

  const copyExportJson = React.useCallback(async () => {
    const json = jsonBufferRef.current || exportConfigToBuffer();
    if (!json) {
      return;
    }
    try {
      await navigator.clipboard.writeText(json);
      setJsonError('');
      setJsonStatus('JSON copié');
    } catch (error) {
      setJsonError('Copie impossible');
      setJsonStatus('');
    }
  }, [exportConfigToBuffer]);

  const downloadExportJson = React.useCallback(() => {
    const json = jsonBufferRef.current || exportConfigToBuffer();
    if (!json) {
      return;
    }
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeLabel = String(draftRef.current?.label || 'generator')
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '');
    link.href = url;
    link.download = `${safeLabel || 'generator'}-config.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setJsonError('');
    setJsonStatus('JSON téléchargé');
  }, [exportConfigToBuffer]);

  const importJsonConfig = React.useCallback(() => {
    const raw = String(jsonBufferRef.current || jsonBuffer || '').trim();
    if (!raw) {
      setJsonError('JSON vide');
      setJsonStatus('');
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      const nextRaw =
        parsed?.config && typeof parsed.config === 'object'
          ? parsed.config
          : parsed;
      if (!nextRaw || typeof nextRaw !== 'object' || Array.isArray(nextRaw)) {
        setJsonError('Format JSON invalide');
        setJsonStatus('');
        return;
      }
      const nextNormalized = normalizeGeneratorConfig(
        {
          ...nextRaw,
          lastResult: null,
        },
        generatorId,
      );
      draftRef.current = nextNormalized;
      setDraft(nextNormalized);
      persist(nextNormalized);
      setJsonError('');
      setJsonStatus('Configuration importée');
    } catch (error) {
      setJsonError('JSON invalide');
      setJsonStatus('');
    }
  }, [generatorId, jsonBuffer, persist]);

  const addControl = () => {
    const controlId = uuid().replaceAll('-', '');
    updateDraft(prev => ({
      ...prev,
      controls: [
        ...(prev.controls || []),
        {
          id: controlId,
          type: 'check',
          label: `Option ${((prev.controls || []).length || 0) + 1}`,
          default: true,
          linkedItemId: null,
        },
      ],
      runtime: {
        ...(prev.runtime || {}),
        controlsState: {
          ...(prev.runtime?.controlsState || {}),
          [controlId]: true,
        },
      },
    }));
  };

  const removeControl = controlId => {
    updateDraft(prev => {
      const nextControls = (prev.controls || []).filter(
        control => control.id !== controlId,
      );
      const nextGroups = (prev.groups || [])
        .map(group => {
          const nextControlIds = (group.controlIds || []).filter(
            id => id !== controlId,
          );
          if (nextControlIds.length < 2) {
            return null;
          }
          return {
            ...group,
            controlIds: nextControlIds,
            fallbackControlId: nextControlIds.includes(group.fallbackControlId)
              ? group.fallbackControlId
              : nextControlIds[0],
          };
        })
        .filter(Boolean);
      const nextProfiles = (prev.profiles || []).map(profile => ({
        ...profile,
        controlIds: (profile.controlIds || []).filter(id => id !== controlId),
        conditionGroups: (profile.conditionGroups || []).map(group => ({
          ...group,
          controlIds: (group.controlIds || []).filter(id => id !== controlId),
        })),
      }));
      const nextControlsState = { ...(prev.runtime?.controlsState || {}) };
      delete nextControlsState[controlId];
      const nextControlsStateWithGroups = applyGeneratorGroupRules(
        nextControlsState,
        nextGroups,
      );
      return {
        ...prev,
        controls: nextControls,
        groups: nextGroups,
        profiles: nextProfiles,
        runtime: {
          ...(prev.runtime || {}),
          controlsState: nextControlsStateWithGroups,
        },
      };
    });
  };

  const addControlInGroup = baseControlId => {
    const newControlId = uuid().replaceAll('-', '');
    updateDraft(prev => {
      const nextControls = [
        ...(prev.controls || []),
        {
          id: newControlId,
          type: 'check',
          label: `Option ${((prev.controls || []).length || 0) + 1}`,
          default: true,
          linkedItemId: null,
        },
      ];

      const existingGroup = (prev.groups || []).find(g =>
        (g.controlIds || []).includes(baseControlId),
      );

      let nextGroups;
      if (existingGroup) {
        nextGroups = (prev.groups || []).map(group =>
          group.id === existingGroup.id
            ? {
                ...group,
                controlIds: Array.from(
                  new Set([...(group.controlIds || []), newControlId]),
                ),
                fallbackControlId: group.fallbackControlId || baseControlId,
              }
            : group,
        );
      } else {
        const groupId = uuid().replaceAll('-', '');
        nextGroups = [
          ...(prev.groups || []),
          {
            id: groupId,
            label: `Groupe ${((prev.groups || []).length || 0) + 1}`,
            displayMode: 'buttons',
            controlIds: [baseControlId, newControlId],
            fallbackControlId: baseControlId,
          },
        ];
      }

      const nextControlsState = {
        ...(prev.runtime?.controlsState || {}),
        [newControlId]: true,
      };
      const nextControlsStateWithGroups = applyGeneratorGroupRules(
        nextControlsState,
        nextGroups,
      );

      return {
        ...prev,
        controls: nextControls,
        groups: nextGroups,
        runtime: {
          ...(prev.runtime || {}),
          controlsState: nextControlsStateWithGroups,
        },
      };
    });
  };

  const toggleGroupOptions = controlId => {
    if (openGroupControlId === controlId) {
      setOpenGroupControlId(null);
      return;
    }
    setOpenGroupControlId(controlId);
    if (groupByControlId[controlId]) {
      return;
    }
    updateDraft(prev => {
      const alreadyGrouped = (prev.groups || []).find(g =>
        (g.controlIds || []).includes(controlId),
      );
      if (alreadyGrouped) {
        return prev;
      }
      const groupId = uuid().replaceAll('-', '');
      return {
        ...prev,
        groups: [
          ...(prev.groups || []),
          {
            id: groupId,
            label: `Groupe ${((prev.groups || []).length || 0) + 1}`,
            displayMode: 'buttons',
            controlIds: [controlId],
            fallbackControlId: controlId,
          },
        ],
      };
    });
  };

  const addProfile = () => {
    const profileId = uuid().replaceAll('-', '');
    const groupId = uuid().replaceAll('-', '');
    updateDraft(prev => ({
      ...prev,
      profiles: [
        ...(prev.profiles || []),
        {
          id: profileId,
          label: `Combinaison ${((prev.profiles || []).length || 0) + 1}`,
          controlIds: [],
          conditionGroups: [
            {
              id: groupId,
              mode: 'all',
              controlIds: [],
            },
          ],
        },
      ],
    }));
  };

  const moveProfile = (profileId, direction) => {
    updateDraft(prev => {
      const profiles = Array.isArray(prev.profiles) ? [...prev.profiles] : [];
      const index = profiles.findIndex(profile => profile.id === profileId);
      if (index === -1) {
        return prev;
      }
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= profiles.length) {
        return prev;
      }
      const tmp = profiles[nextIndex];
      profiles[nextIndex] = profiles[index];
      profiles[index] = tmp;
      return {
        ...prev,
        profiles,
      };
    });
  };

  const removeProfile = profileId => {
    updateDraft(prev => {
      const nextProfiles = (prev.profiles || []).filter(
        profile => profile.id !== profileId,
      );
      return {
        ...prev,
        profiles: nextProfiles,
      };
    });
  };

  const updateProfileGroups = (profileId, updater) => {
    updateDraft(prev => ({
      ...prev,
      profiles: (prev.profiles || []).map(profile => {
        if (profile.id !== profileId) {
          return profile;
        }
        const currentGroups = Array.isArray(profile.conditionGroups)
          ? profile.conditionGroups
          : [];
        const nextGroups = updater(currentGroups);
        const normalizedGroups = Array.isArray(nextGroups) ? nextGroups : [];
        const mergedControlIds = Array.from(
          new Set(
            normalizedGroups.flatMap(group =>
              Array.isArray(group?.controlIds) ? group.controlIds : [],
            ),
          ),
        );
        return {
          ...profile,
          conditionGroups: normalizedGroups,
          controlIds: mergedControlIds,
        };
      }),
    }));
  };

  const searchControlLinkItems = React.useCallback(
    async (controlId, value) => {
      const query = value?.trim?.() || '';
      if (!controlId) {
        return;
      }
      if (!query) {
        setControlLinkResultsById(prev => ({ ...prev, [controlId]: [] }));
        return;
      }
      const res = await getSearch(
        query,
        item,
        searchableTemplateIn,
        undefined,
      );
      const nextResults = Array.isArray(res)
        ? res.filter(result => result?._id && result._id !== itemId)
        : [];
      setControlLinkResultsById(prev => ({
        ...prev,
        [controlId]: nextResults,
      }));
    },
    [item, itemId, searchableTemplateIn],
  );

  const groupByControlId = React.useMemo(() => {
    const map = {};
    (draft.groups || []).forEach(group => {
      (group.controlIds || []).forEach(controlId => {
        map[controlId] = group;
      });
    });
    return map;
  }, [draft.groups]);

  const visibleControls = React.useMemo(() => {
    const groupedControlIds = new Set();
    const leaderControlIds = new Set();
    (draft.groups || []).forEach(group => {
      const ids = Array.isArray(group.controlIds)
        ? group.controlIds.filter(Boolean)
        : [];
      ids.forEach(id => groupedControlIds.add(id));
      if (ids[0]) {
        leaderControlIds.add(ids[0]);
      }
    });

    return (draft.controls || []).filter(control => {
      if (!groupedControlIds.has(control.id)) {
        return true;
      }
      return leaderControlIds.has(control.id);
    });
  }, [draft.controls, draft.groups]);

  const filteredProfiles = React.useMemo(() => {
    const profiles = Array.isArray(draft?.profiles) ? draft.profiles : [];
    const query = profileSearchQuery.trim().toLowerCase();
    if (!query) {
      return profiles;
    }

    const controlsById = Object.fromEntries(
      (draft.controls || []).map(control => [control.id, control]),
    );

    return profiles.filter(profile => {
      const profileLabel = String(profile?.label || '').toLowerCase();
      if (profileLabel.includes(query)) {
        return true;
      }
      const conditionGroups = Array.isArray(profile?.conditionGroups)
        ? profile.conditionGroups
        : [];
      return conditionGroups.some(group =>
        (group?.controlIds || []).some(controlId =>
          String(controlsById?.[controlId]?.label || '')
            .toLowerCase()
            .includes(query),
        ),
      );
    });
  }, [draft.controls, draft?.profiles, profileSearchQuery]);

  const moveControlBlock = (controlId, direction) => {
    updateDraft(prev => {
      const controls = Array.isArray(prev.controls) ? [...prev.controls] : [];
      if (controls.length <= 1) {
        return prev;
      }
      const groups = Array.isArray(prev.groups) ? prev.groups : [];
      const groupedIds = new Set();
      groups.forEach(group => {
        (group?.controlIds || []).forEach(id => groupedIds.add(id));
      });

      const idToIndex = {};
      controls.forEach((control, index) => {
        if (control?.id) {
          idToIndex[control.id] = index;
        }
      });

      const consumed = new Set();
      const blocks = [];
      controls.forEach(control => {
        const id = control?.id;
        if (!id || consumed.has(id)) {
          return;
        }
        if (!groupedIds.has(id)) {
          blocks.push([id]);
          consumed.add(id);
          return;
        }
        const group = groups.find(entry => (entry?.controlIds || []).includes(id));
        if (!group) {
          blocks.push([id]);
          consumed.add(id);
          return;
        }
        const groupIds = (group.controlIds || [])
          .filter(groupId => Object.prototype.hasOwnProperty.call(idToIndex, groupId))
          .sort((a, b) => idToIndex[a] - idToIndex[b]);
        if (groupIds.length === 0) {
          blocks.push([id]);
          consumed.add(id);
          return;
        }
        groupIds.forEach(groupId => consumed.add(groupId));
        blocks.push(groupIds);
      });

      const blockIndex = blocks.findIndex(block => block.includes(controlId));
      if (blockIndex === -1) {
        return prev;
      }
      const targetIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
      if (targetIndex < 0 || targetIndex >= blocks.length) {
        return prev;
      }
      const nextBlocks = [...blocks];
      const tmp = nextBlocks[targetIndex];
      nextBlocks[targetIndex] = nextBlocks[blockIndex];
      nextBlocks[blockIndex] = tmp;

      const reorderedIds = nextBlocks.flat();
      const controlById = {};
      controls.forEach(control => {
        if (control?.id) {
          controlById[control.id] = control;
        }
      });
      const reorderedControls = reorderedIds
        .map(id => controlById[id])
        .filter(Boolean);

      if (reorderedControls.length !== controls.length) {
        return prev;
      }

      return {
        ...prev,
        controls: reorderedControls,
      };
    });
  };

  React.useEffect(() => {
    if (!openGroupControlId) {
      return;
    }
    const exists = (draft.controls || []).some(
      control => control.id === openGroupControlId,
    );
    if (!exists) {
      setOpenGroupControlId(null);
    }
  }, [openGroupControlId, draft.controls]);

  return (
    <ModalGlobal
      idToOpen={generatorId}
      title='Générateur'
    >
      <WidgetSettingsRoot>
        <WidgetSettingsCollapsibleSection
          title='général'
          defaultOpen={true}
        >
          <SettingsRow label='nom'>
            <SettingsInput
              resetValue={draft.label || ''}
              onChange={event =>
                setDraft(prev => ({ ...prev, label: event.target.value }))
              }
              onBlur={() => persist(draft)}
            />
          </SettingsRow>
          <div className='mt-05 flex flex-col gap-03'>
            <Checkbox
              label="autoriser la création d'item"
              checked={draft?.itemCreation?.enabled !== false}
              disabled={!canEdit}
              onChange={checked =>
                updateDraft(prev => ({
                  ...prev,
                  itemCreation: {
                    ...(prev.itemCreation || {}),
                    enabled: checked,
                  },
                }))
              }
            />
            <Checkbox
              label="ouvrir l'item créé dans la sidebar"
              checked={draft?.itemCreation?.openInSidebar !== false}
              disabled={!canEdit}
              onChange={checked =>
                updateDraft(prev => ({
                  ...prev,
                  itemCreation: {
                    ...(prev.itemCreation || {}),
                    openInSidebar: checked,
                  },
                }))
              }
            />
          </div>
          <div className='mt-05 flex flex-col gap-05'>
            <div className='text-xs uppercase text-contrast text-opacity-70'>
              template item
            </div>
            <InputSimple
              className='m-0'
              resetValue={templateQuery}
              placeholder={
                templateItem?._id
                  ? `Template: ${templateItem.label || templateItem._id}`
                  : 'chercher un item template'
              }
              onChange={event => {
                const value = event.target.value;
                setTemplateQuery(value);
                searchTemplateItems(value);
              }}
              onBlur={() => null}
            />
            {templateResults.length > 0 && (
              <div className='max-h-8 overflow-y-auto rounded bg-main bg-opacity-30 p-05'>
                {templateResults.map(result => (
                  <div
                    key={result._id}
                    className='cursor-pointer rounded px-05 py-03 transition hover:bg-main hover:bg-opacity-40'
                    onClick={() => {
                      setTemplateResults([]);
                      setTemplateQuery('');
                      updateDraft(prev => ({
                        ...prev,
                        itemCreation: {
                          ...(prev.itemCreation || {}),
                          templateItemId: result._id,
                        },
                      }));
                    }}
                  >
                    {result.label}
                  </div>
                ))}
              </div>
            )}
            {templateItem?._id && (
              <div className='flex items-center justify-between gap-05 rounded bg-main bg-opacity-20 p-05 text-xs'>
                <span>
                  template sélectionné: {templateItem.label || templateItem._id}
                </span>
                <BtnSimple
                  className='text-xs bg-secondary-d border border-secondary-d'
                  onDoubleClick={() =>
                    updateDraft(prev => ({
                      ...prev,
                      itemCreation: {
                        ...(prev.itemCreation || {}),
                        templateItemId: null,
                        outputMapping: {},
                      },
                    }))
                  }
                  disabled={!canEdit}
                  title='double-clic pour retirer'
                >
                  <FaTrash />
                </BtnSimple>
              </div>
            )}
            {draft?.itemCreation?.templateItemId && (
              <WidgetSettingsCollapsibleSection
                title='mapping template'
                help="Mappe chaque sortie générée à un champ du template. Si le champ n'existe pas, un nouveau champ sera créé."
                defaultOpen={false}
              >
                <div className='flex flex-col gap-05'>
                  {(draft.outputs || []).map(output => {
                    const outputKind = output?.target?.kind || 'text';
                    const outputId = output?.id || '';
                    const mapping =
                      draft?.itemCreation?.outputMapping?.[outputId] || {};
                    const defaultKind =
                      outputKind === 'item_label'
                        ? 'label'
                        : outputKind === 'num'
                          ? 'num'
                          : 'text';
                    const mappingKind = mapping?.kind || defaultKind;
                    const templateTexts = Array.isArray(templateItem?.text)
                      ? templateItem.text
                      : [];
                    const templateNums = Array.isArray(templateItem?.has_num)
                      ? templateItem.has_num
                      : [];
                    const compatibleFields =
                      mappingKind === 'text'
                        ? templateTexts
                        : mappingKind === 'num'
                          ? templateNums
                          : [];

                    return (
                      <div
                        key={`mapping:${outputId}`}
                        className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex items-center gap-05'
                      >
                        <div className='flex-1 min-w-0'>
                          <div className='text-xs uppercase text-contrast text-opacity-60'>
                            {outputKind}
                          </div>
                          <div className='truncate'>{output?.label || outputId}</div>
                        </div>
                        <div className='w-140'>
                          <SettingsSelect
                            value={mappingKind}
                            disabled={!canEdit}
                            onChange={event => {
                              const nextKind = event.target.value;
                              updateDraft(prev => ({
                                ...prev,
                                itemCreation: {
                                  ...(prev.itemCreation || {}),
                                  outputMapping: {
                                    ...(prev.itemCreation?.outputMapping || {}),
                                    [outputId]: {
                                      kind: nextKind,
                                      fieldId: null,
                                    },
                                  },
                                },
                              }));
                            }}
                          >
                            <option value='label'>label</option>
                            <option value='text'>texte</option>
                            <option value='num'>num</option>
                          </SettingsSelect>
                        </div>
                        {mappingKind !== 'label' && (
                          <div className='w-220'>
                            <SettingsSelect
                              value={mapping?.fieldId || 'new'}
                              disabled={!canEdit}
                              onChange={event => {
                                const nextFieldId =
                                  event.target.value === 'new'
                                    ? null
                                    : event.target.value;
                                updateDraft(prev => ({
                                  ...prev,
                                  itemCreation: {
                                    ...(prev.itemCreation || {}),
                                    outputMapping: {
                                      ...(prev.itemCreation?.outputMapping || {}),
                                      [outputId]: {
                                        kind: mappingKind,
                                        fieldId: nextFieldId,
                                      },
                                    },
                                  },
                                }));
                              }}
                            >
                              <option value='new'>nouveau champ</option>
                              {compatibleFields.map(fieldObj => {
                                const fieldId = getId(fieldObj);
                                const fieldData = fieldObj?.[fieldId] || {};
                                const label =
                                  mappingKind === 'num'
                                    ? fieldData?.label || fieldId
                                    : fieldData?.text || fieldId;
                                return (
                                  <option
                                    key={`map:${outputId}:${fieldId}`}
                                    value={fieldId}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </SettingsSelect>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(draft.outputs || []).length === 0 && (
                    <div className='text-xs text-contrast text-opacity-60'>
                      aucune sortie configurée
                    </div>
                  )}
                </div>
              </WidgetSettingsCollapsibleSection>
            )}
          </div>
        </WidgetSettingsCollapsibleSection>

        <WidgetSettingsCollapsibleSection
          title='import / export JSON'
          help='Exporte ou importe la configuration complète du générateur.'
          defaultOpen={false}
        >
          <div className='flex flex-wrap gap-05 mb-05'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={exportConfigToBuffer}
              disabled={!canEdit}
            >
              exporter dans l’éditeur
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={copyExportJson}
              disabled={!canEdit}
            >
              copier JSON
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={downloadExportJson}
              disabled={!canEdit}
            >
              télécharger JSON
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={importJsonConfig}
              disabled={!canEdit}
            >
              importer JSON
            </BtnSimple>
          </div>
          <SettingsTextArea
            className='min-h-160 font-mono text-xs'
            resetValue={jsonBuffer}
            placeholder='Collez ici la configuration JSON du générateur'
            onChange={event => {
              const nextRaw = event.target.value;
              setJsonBufferValue(nextRaw);
              const trimmed = String(nextRaw || '').trim();
              if (!trimmed) {
                setJsonError('');
                setJsonStatus('');
                return;
              }
              try {
                JSON.parse(trimmed);
                setJsonError('');
                setJsonStatus('JSON valide, prêt à importer');
              } catch (error) {
                setJsonStatus('');
                setJsonError('JSON invalide');
              }
            }}
            onBlur={value => {
              setJsonBufferValue(value);
              const trimmed = String(value || '').trim();
              if (!trimmed) {
                return;
              }
              try {
                JSON.parse(trimmed);
                importJsonConfig();
              } catch (error) {
                // keep explicit import button/error state behavior
              }
            }}
          />
          {jsonError ? (
            <div className='mt-03 text-xs text-red'>{jsonError}</div>
          ) : null}
          {jsonStatus ? (
            <div className='mt-03 text-xs text-contrast text-opacity-70'>
              {jsonStatus}
            </div>
          ) : null}
        </WidgetSettingsCollapsibleSection>

        <WidgetSettingsCollapsibleSection
          title='contrôles'
          help='Chaque ligne correspond à un contrôle. Ouvre "options de groupe" pour gérer les contrôles exclusifs.'
          defaultOpen={false}
        >
          <div className='flex flex-col gap-05'>
            {visibleControls.map(control => (
              <div
                key={control.id}
                className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
              >
                {(() => {
                  const group = groupByControlId[control.id] || null;
                  const isGroupLeader =
                    group &&
                    Array.isArray(group.controlIds) &&
                    group.controlIds[0] === control.id;
                  const inputValue = isGroupLeader
                    ? group?.label || ''
                    : control?.label || '';
                  return (
                <div className='flex items-center gap-05'>
                  <div className='flex-1'>
                    <SettingsInput
                      resetValue={inputValue}
                      onChange={event => {
                        const nextValue = event.target.value;
                        if (isGroupLeader && group?.id) {
                          setDraft(prev => ({
                            ...prev,
                            groups: (prev.groups || []).map(entry =>
                              entry.id === group.id
                                ? { ...entry, label: nextValue }
                                : entry,
                            ),
                          }));
                          return;
                        }
                        setDraft(prev => ({
                          ...prev,
                          controls: (prev.controls || []).map(entry =>
                            entry.id === control.id
                              ? { ...entry, label: nextValue }
                              : entry,
                          ),
                        }));
                      }}
                      onBlur={() => persist(draft)}
                    />
                  </div>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() => moveControlBlock(control.id, 'up')}
                    disabled={!canEdit || visibleControls[0]?.id === control.id}
                  >
                    ↑
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() => moveControlBlock(control.id, 'down')}
                    disabled={
                      !canEdit ||
                      visibleControls[visibleControls.length - 1]?.id === control.id
                    }
                  >
                    ↓
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() => toggleGroupOptions(control.id)}
                    disabled={!canEdit}
                  >
                    options de groupe
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-secondary-d border border-secondary-d'
                    onDoubleClick={() => removeControl(control.id)}
                    disabled={!canEdit || (draft.controls || []).length <= 1}
                    title='double-clic pour supprimer'
                  >
                    <FaTrash />
                  </BtnSimple>
                </div>
                  );
                })()}

                {!groupByControlId[control.id] && (
                  <div className='rounded border border-primary border-opacity-15 bg-main bg-opacity-15 p-05'>
                    <div className='text-xs uppercase text-contrast text-opacity-70 mb-03'>
                      item lié (optionnel)
                    </div>
                    <InputSimple
                      className='m-0'
                      resetValue={controlLinkQueryById[control.id] || ''}
                      placeholder={
                        control?.linkedItemId
                          ? `lié: ${
                              allItems?.[control.linkedItemId]?.label ||
                              control.linkedItemId
                            }`
                          : 'chercher un item à lier'
                      }
                      onChange={event => {
                        const value = event.target.value;
                        setControlLinkQueryById(prev => ({
                          ...prev,
                          [control.id]: value,
                        }));
                        searchControlLinkItems(control.id, value);
                      }}
                      onBlur={() => null}
                    />
                    {(controlLinkResultsById[control.id] || []).length > 0 && (
                      <div className='mt-03 max-h-8 overflow-y-auto rounded bg-main bg-opacity-30 p-05'>
                        {(controlLinkResultsById[control.id] || []).map(result => (
                          <div
                            key={`${control.id}:linked:${result._id}`}
                            className='cursor-pointer rounded px-05 py-03 transition hover:bg-main hover:bg-opacity-40'
                            onClick={() => {
                              setControlLinkResultsById(prev => ({
                                ...prev,
                                [control.id]: [],
                              }));
                              setControlLinkQueryById(prev => ({
                                ...prev,
                                [control.id]: '',
                              }));
                              updateDraft(prev => ({
                                ...prev,
                                controls: (prev.controls || []).map(entry =>
                                  entry.id === control.id
                                    ? { ...entry, linkedItemId: result._id }
                                    : entry,
                                ),
                              }));
                            }}
                          >
                            {result.label}
                          </div>
                        ))}
                      </div>
                    )}
                    {control?.linkedItemId && (
                      <div className='mt-03 flex items-center justify-between gap-05 text-xs text-contrast text-opacity-70'>
                        <span>
                          item lié:{' '}
                          {allItems?.[control.linkedItemId]?.label ||
                            control.linkedItemId}
                        </span>
                        <BtnSimple
                          className='text-xs bg-secondary-d border border-secondary-d'
                          disabled={!canEdit}
                          onDoubleClick={() =>
                            updateDraft(prev => ({
                              ...prev,
                              controls: (prev.controls || []).map(entry =>
                                entry.id === control.id
                                  ? { ...entry, linkedItemId: null }
                                  : entry,
                              ),
                            }))
                          }
                          title='double-clic pour retirer'
                        >
                          <FaTrash />
                        </BtnSimple>
                      </div>
                    )}
                  </div>
                )}

                {openGroupControlId === control.id && groupByControlId[control.id] && (
                    <div className='mt-02 rounded border border-primary border-opacity-15 bg-main bg-opacity-15 p-05 flex flex-col gap-05'>
                      <div className='flex items-center gap-05'>
                        <div className='text-xs uppercase text-contrast text-opacity-70'>
                          affichage
                        </div>
                        <div className='w-140'>
                          <SettingsSelect
                            value={
                              groupByControlId[control.id].displayMode || 'buttons'
                            }
                            disabled={!canEdit}
                            onChange={event =>
                              updateDraft(prev => ({
                                ...prev,
                                groups: (prev.groups || []).map(entry =>
                                  entry.id === groupByControlId[control.id].id
                                    ? {
                                        ...entry,
                                        displayMode:
                                          event.target.value === 'checkbox'
                                            ? 'checkbox'
                                            : event.target.value === 'dropdown'
                                              ? 'dropdown'
                                              : 'buttons',
                                      }
                                    : entry,
                                ),
                              }))
                            }
                          >
                            <option value='buttons'>boutons</option>
                            <option value='checkbox'>checkbox</option>
                            <option
                              value='dropdown'
                              disabled={
                                (groupByControlId[control.id].controlIds || [])
                                  .length < 2
                              }
                            >
                              dropdown
                            </option>
                          </SettingsSelect>
                        </div>
                      </div>
                      <div className='flex flex-col gap-03'>
                        {(groupByControlId[control.id].controlIds || []).map(
                          groupControlId => {
                            const groupControl = (draft.controls || []).find(
                              entry => entry.id === groupControlId,
                            );
                            if (!groupControl) {
                              return null;
                            }
                            return (
                              <div
                                key={`${groupByControlId[control.id].id}:${groupControlId}`}
                                className='flex flex-col gap-03'
                              >
                                <div className='flex items-center gap-05'>
                                  <Checkbox
                                    type='radio'
                                    label='défaut'
                                    checked={
                                      groupByControlId[control.id]
                                        .fallbackControlId === groupControlId
                                    }
                                    disabled={!canEdit}
                                    onChange={() => {
                                      const nextFallbackId = groupControlId;
                                      updateDraft(prev => {
                                        const nextGroups = (prev.groups || []).map(
                                          entry =>
                                            entry.id ===
                                            groupByControlId[control.id].id
                                              ? {
                                                  ...entry,
                                                  fallbackControlId: (
                                                    entry.controlIds || []
                                                  ).includes(nextFallbackId)
                                                    ? nextFallbackId
                                                    : null,
                                                }
                                              : entry,
                                        );
                                        const nextControlsState =
                                          applyGeneratorGroupRules(
                                            prev.runtime?.controlsState || {},
                                            nextGroups,
                                          );
                                        return {
                                          ...prev,
                                          groups: nextGroups,
                                          runtime: {
                                            ...(prev.runtime || {}),
                                            controlsState: nextControlsState,
                                          },
                                        };
                                      });
                                    }}
                                  />
                                  <div className='flex-1'>
                                    <SettingsInput
                                      resetValue={groupControl.label || ''}
                                      onChange={event =>
                                        setDraft(prev => ({
                                          ...prev,
                                          controls: (prev.controls || []).map(
                                            entry =>
                                              entry.id === groupControlId
                                                ? {
                                                    ...entry,
                                                    label: event.target.value,
                                                  }
                                                : entry,
                                          ),
                                        }))
                                      }
                                      onBlur={() => persist(draft)}
                                    />
                                  </div>
                                  <BtnSimple
                                    className='text-xs bg-secondary-d border border-secondary-d'
                                    onDoubleClick={() => removeControl(groupControlId)}
                                    disabled={
                                      !canEdit ||
                                      (groupByControlId[control.id].controlIds ||
                                        []).length <= 1
                                    }
                                    title='double-clic pour supprimer'
                                  >
                                    <FaTrash />
                                  </BtnSimple>
                                </div>
                                <InputSimple
                                  className='m-0'
                                  resetValue={controlLinkQueryById[groupControlId] || ''}
                                  placeholder={
                                    groupControl?.linkedItemId
                                      ? `lié: ${
                                          allItems?.[groupControl.linkedItemId]?.label ||
                                          groupControl.linkedItemId
                                        }`
                                      : 'lier un item (optionnel)'
                                  }
                                  onChange={event => {
                                    const value = event.target.value;
                                    setControlLinkQueryById(prev => ({
                                      ...prev,
                                      [groupControlId]: value,
                                    }));
                                    searchControlLinkItems(groupControlId, value);
                                  }}
                                  onBlur={() => null}
                                />
                                {(controlLinkResultsById[groupControlId] || []).length > 0 && (
                                  <div className='max-h-6 overflow-y-auto rounded bg-main bg-opacity-30 p-03'>
                                    {(controlLinkResultsById[groupControlId] || []).map(
                                      result => (
                                        <div
                                          key={`${groupControlId}:linked:${result._id}`}
                                          className='cursor-pointer rounded px-03 py-02 transition hover:bg-main hover:bg-opacity-40'
                                          onClick={() => {
                                            setControlLinkResultsById(prev => ({
                                              ...prev,
                                              [groupControlId]: [],
                                            }));
                                            setControlLinkQueryById(prev => ({
                                              ...prev,
                                              [groupControlId]: '',
                                            }));
                                            updateDraft(prev => ({
                                              ...prev,
                                              controls: (prev.controls || []).map(entry =>
                                                entry.id === groupControlId
                                                  ? { ...entry, linkedItemId: result._id }
                                                  : entry,
                                              ),
                                            }));
                                          }}
                                        >
                                          {result.label}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>

                      <div className='flex justify-end'>
                        <BtnSimple
                          className='text-xs bg-primary'
                          onClick={() => addControlInGroup(control.id)}
                          disabled={!canEdit}
                        >
                          ajouter un contrôle
                        </BtnSimple>
                      </div>

                      <div className='text-2xs text-contrast text-opacity-60'>
                        groupe exclusif: un seul contrôle actif à la fois
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
          <div className='flex justify-end mt-05'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={addControl}
              disabled={!canEdit}
            >
              nouveau contrôle
            </BtnSimple>
          </div>
        </WidgetSettingsCollapsibleSection>

        <WidgetSettingsCollapsibleSection
          title="combinaisons d'options"
          help='Configure les combinaisons de contrôles.'
          defaultOpen={false}
        >
          <div className='mb-05'>
            <SettingsInput
              resetValue={profileSearchQuery}
              placeholder='rechercher une combinaison ou une option'
              onChange={event => setProfileSearchQuery(event.target.value)}
              onBlur={() => null}
            />
          </div>
          <div className='flex flex-col gap-05'>
            {filteredProfiles.map(profile => (
              <div
                key={profile.id}
                className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
              >
                <div className='flex items-center gap-05'>
                  <div className='flex-1'>
                    <SettingsInput
                      resetValue={profile.label || ''}
                      onChange={event =>
                        setDraft(prev => ({
                          ...prev,
                          profiles: (prev.profiles || []).map(entry =>
                            entry.id === profile.id
                              ? { ...entry, label: event.target.value }
                              : entry,
                          ),
                        }))
                      }
                      onBlur={() => persist(draft)}
                    />
                  </div>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() => moveProfile(profile.id, 'up')}
                    disabled={!canEdit || (draft.profiles || [])[0]?.id === profile.id}
                  >
                    ↑
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() => moveProfile(profile.id, 'down')}
                    disabled={
                      !canEdit ||
                      (draft.profiles || [])[((draft.profiles || []).length || 1) - 1]
                        ?.id === profile.id
                    }
                  >
                    ↓
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() =>
                      dispatch(
                        setOpenModales({
                          id: `generator-level2:${generatorId}:${profile.id}`,
                          open: true,
                        }),
                      )
                    }
                    disabled={!canEdit}
                  >
                    génération
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-primary'
                    onClick={() =>
                      setOpenProfileOptionsById(prev => ({
                        ...prev,
                        [profile.id]: !prev[profile.id],
                      }))
                    }
                    disabled={!canEdit}
                  >
                    options
                  </BtnSimple>
                  <BtnSimple
                    className='text-xs bg-secondary-d border border-secondary-d'
                    onDoubleClick={() => removeProfile(profile.id)}
                    disabled={!canEdit || (draft.profiles || []).length <= 1}
                    title='double-clic pour supprimer'
                  >
                    <FaTrash />
                  </BtnSimple>
                </div>

                {openProfileOptionsById[profile.id] && (
                  <div className='flex flex-col gap-03'>
                    {(Array.isArray(profile.conditionGroups)
                      ? profile.conditionGroups
                      : []
                    ).map((group, groupIndex, groupsArray) => (
                      <div
                        key={`${profile.id}:${group.id || groupIndex}`}
                        className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-03'
                      >
                        <div className='flex items-center gap-05'>
                          <div className='w-px72'>
                            <SettingsSelect
                              value={group?.mode === 'any' ? 'any' : 'all'}
                              onChange={event =>
                                updateProfileGroups(profile.id, currentGroups =>
                                  currentGroups.map(entry =>
                                    entry.id === group.id
                                      ? {
                                          ...entry,
                                          mode:
                                            event.target.value === 'any'
                                              ? 'any'
                                              : 'all',
                                        }
                                      : entry,
                                  ),
                                )
                              }
                              disabled={!canEdit}
                            >
                              <option value='all'>et</option>
                              <option value='any'>ou</option>
                            </SettingsSelect>
                          </div>
                          <BtnSimple
                            className='text-xs bg-secondary-d border border-secondary-d'
                            onDoubleClick={() =>
                              updateProfileGroups(profile.id, currentGroups =>
                                currentGroups.filter(
                                  entry => entry.id !== group.id,
                                ),
                              )
                            }
                            disabled={!canEdit || groupsArray.length <= 1}
                            title='double-clic pour supprimer'
                          >
                            <FaTrash />
                          </BtnSimple>
                        </div>
                        {(draft.controls || []).map(control => (
                          <Checkbox
                            key={`${profile.id}:${group.id}:${control.id}`}
                            label={control.label || 'Option'}
                            checked={(group.controlIds || []).includes(control.id)}
                            onChange={checked =>
                              updateProfileGroups(profile.id, currentGroups =>
                                currentGroups.map(entry => {
                                  if (entry.id !== group.id) {
                                    return entry;
                                  }
                                  const currentIds = Array.isArray(entry.controlIds)
                                    ? entry.controlIds
                                    : [];
                                  const nextIds = checked
                                    ? Array.from(
                                        new Set([...currentIds, control.id]),
                                      )
                                    : currentIds.filter(
                                        id => id !== control.id,
                                      );
                                  return {
                                    ...entry,
                                    controlIds: nextIds,
                                  };
                                }),
                              )
                            }
                          />
                        ))}
                      </div>
                    ))}
                    <div className='flex justify-end'>
                      <BtnSimple
                        className='text-xs bg-primary'
                        onClick={() =>
                          updateProfileGroups(profile.id, currentGroups => [
                            ...currentGroups,
                            {
                              id: uuid().replaceAll('-', ''),
                              mode: 'all',
                              controlIds: [],
                            },
                          ])
                        }
                        disabled={!canEdit}
                      >
                        nouveau groupe
                      </BtnSimple>
                    </div>
                  </div>
                )}
                <GeneratorSettingsLevel2
                  generatorId={generatorId}
                  profile={profile}
                  draft={draft}
                  updateDraft={updateDraft}
                  canEdit={canEdit}
                  generatorOptions={generatorOptions}
                />
              </div>
            ))}
            {filteredProfiles.length === 0 && (
              <div className='text-xs text-contrast text-opacity-60'>
                aucune combinaison trouvée
              </div>
            )}
          </div>

          <div className='flex justify-end mt-05'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={addProfile}
              disabled={!canEdit}
            >
              nouvelle combinaison
            </BtnSimple>
          </div>
        </WidgetSettingsCollapsibleSection>
      </WidgetSettingsRoot>
    </ModalGlobal>
  );
};
